'use client'

import * as React from 'react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  ScrollArea,
  ScrollBar,
  cn
} from '../../../../components/ui';
import { Database } from 'lucide-react';
import { SchemaResponse } from '../types';

/**
 * Schema Result Component
 * 
 * Displays the schema information in a formatted accordion.
 */
export function SchemaResult({ result }: { result: SchemaResponse }) {
  if (!result.success) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
        <p className="font-semibold">Error retrieving schema:</p>
        <p>{result.error}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="bg-muted/30 px-3 py-2 border-b flex items-center">
        <Database className="h-4 w-4 text-muted-foreground mr-2" />
        <span className="text-sm font-medium text-foreground">Schema Information</span>
      </div>
      <div className="p-3">
        <ScrollArea className="h-[200px] overflow-auto" type="always">
          <div className="pr-3">
            <Accordion type="single" collapsible>
              {result.tables?.map((table) => (
                <AccordionItem key={table.name} value={table.name}>
                  <AccordionTrigger className="text-sm hover:no-underline py-2">
                    <div className="flex items-center">
                      <Badge variant="outline" className="mr-2 bg-primary/10 text-primary">
                        {table.name}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {table.columns.length} columns
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-2 border-l-2 border-muted space-y-1">
                      <div className="grid grid-cols-2 gap-2 mb-2 text-xs font-medium text-muted-foreground">
                        <div className="truncate">Column Name</div>
                        <div className="truncate">Type</div>
                      </div>
                      {table.columns.map((col) => (
                        <div key={col.name} className="grid grid-cols-2 gap-2 text-sm py-1 hover:bg-muted/20 rounded px-1">
                          <div className="font-medium truncate text-foreground" title={col.name}>
                            {col.name}
                            {col.nullable === false && (
                              <span className="ml-1 text-xs text-muted-foreground">*</span>
                            )}
                          </div>
                          <div className="truncate">
                            <Badge variant="secondary" className="text-xs truncate">
                              {col.type}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <ScrollBar orientation="horizontal" />
          <ScrollBar orientation="vertical" />
        </ScrollArea>
      </div>
    </div>
  );
} 